"use client";

/**
 * NewForm design-system VideoScrubber.
 *
 * A seek bar and time readout for a `<video>` the caller already renders:
 * drag or click to jump, arrow keys to step a second. It reads the element
 * through a ref rather than owning playback, so any player can add seeking
 * without changing how it plays.
 *
 * Interaction follows the creator-portal review player: the head tracks the
 * pointer 1:1 from pointer-down, with pointer capture so a drag continues past
 * the bar's bounds, and a visually hidden native range input carries the
 * slider semantics and arrow-key stepping, because the painted track cannot be
 * tabbed to.
 *
 * It sits over media, which is dark in every theme, so its colours are pinned
 * white instead of taken from a theme role — the same call as the chips on
 * `<AdMediaPreview>`.
 *
 *   const videoRef = useRef<HTMLVideoElement>(null);
 *   <video ref={videoRef} src={url} />
 *   <VideoScrubber
 *     videoRef={videoRef}
 *     mediaKey={url}                    // re-reads duration when the source changes
 *     className="absolute inset-x-0 bottom-0"
 *   />
 *
 * No matching reference page in packages/nf-design-system/preview yet.
 */

import {
  type PointerEvent,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { cn } from "@/lib/utils";

const KEY_STEP_SECONDS = 1;

/** `m:ss`, the way a player reads out a position. */
export function formatVideoTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const whole = Math.floor(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

/** The position under the pointer, as seconds into a video of `duration`. */
export function timeAtPointer(
  clientX: number,
  track: { left: number; width: number },
  duration: number,
): number {
  if (track.width <= 0 || duration <= 0) return 0;
  const ratio = (clientX - track.left) / track.width;
  return Math.min(Math.max(ratio, 0), 1) * duration;
}

export interface VideoScrubberProps {
  videoRef: RefObject<HTMLVideoElement | null>;
  /** Anything that changes with the source (its URL), so a new video is re-read. */
  mediaKey?: string;
  className?: string;
}

export function VideoScrubber({
  videoRef,
  mediaKey,
  className,
}: VideoScrubberProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [scrubbing, setScrubbing] = useState(false);

  // `timeupdate` fires about four times a second, which reads as a stutter on
  // a bar this wide; follow the playhead every frame while it plays instead.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let frame = 0;
    const readDuration = () =>
      setDuration(Number.isFinite(video.duration) ? video.duration : 0);
    const readTime = () => setCurrentTime(video.currentTime);
    const follow = () => {
      readTime();
      frame = requestAnimationFrame(follow);
    };
    const start = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(follow);
    };
    const stop = () => {
      cancelAnimationFrame(frame);
      readTime();
    };

    readDuration();
    readTime();
    if (!video.paused) start();
    video.addEventListener("loadedmetadata", readDuration);
    video.addEventListener("durationchange", readDuration);
    video.addEventListener("seeked", readTime);
    video.addEventListener("play", start);
    video.addEventListener("pause", stop);
    return () => {
      cancelAnimationFrame(frame);
      video.removeEventListener("loadedmetadata", readDuration);
      video.removeEventListener("durationchange", readDuration);
      video.removeEventListener("seeked", readTime);
      video.removeEventListener("play", start);
      video.removeEventListener("pause", stop);
    };
  }, [videoRef, mediaKey]);

  const seekTo = useCallback(
    (seconds: number) => {
      const video = videoRef.current;
      if (!video || duration <= 0) return;
      const next = Math.min(Math.max(seconds, 0), duration);
      video.currentTime = next;
      setCurrentTime(next);
    },
    [videoRef, duration],
  );

  const seekToPointer = (event: PointerEvent<HTMLDivElement>) => {
    const track = trackRef.current;
    if (!track) return;
    seekTo(
      timeAtPointer(event.clientX, track.getBoundingClientRect(), duration),
    );
  };

  const percent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      className={cn(
        "flex items-center gap-nf-ds-4 bg-gradient-to-t from-black/60 to-transparent px-nf-ds-3 pb-nf-ds-3 pt-nf-ds-8",
        className,
      )}
    >
      <input
        type="range"
        min={0}
        max={duration}
        step={KEY_STEP_SECONDS}
        value={Math.min(currentTime, duration)}
        disabled={duration <= 0}
        onChange={(event) => seekTo(Number(event.target.value))}
        // The frame around a preview toggles playback on click, Enter and
        // Space; stepping the slider must not reach it.
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        aria-label="Seek"
        aria-valuetext={`${formatVideoTime(currentTime)} of ${formatVideoTime(duration)}`}
        className="peer sr-only"
      />
      <div
        ref={trackRef}
        aria-hidden
        className="relative flex h-nf-ds-6 flex-1 cursor-pointer touch-none items-center rounded-nf-ds-pill peer-focus-visible:ring-2 peer-focus-visible:ring-white"
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => {
          event.stopPropagation();
          if (duration <= 0) return;
          event.currentTarget.setPointerCapture(event.pointerId);
          setScrubbing(true);
          seekToPointer(event);
        }}
        onPointerMove={(event) => {
          if (scrubbing) seekToPointer(event);
        }}
        onPointerUp={(event) => {
          event.currentTarget.releasePointerCapture(event.pointerId);
          setScrubbing(false);
        }}
        onPointerCancel={() => setScrubbing(false)}
      >
        <div className="h-nf-ds-1 w-full overflow-hidden rounded-nf-ds-pill bg-white/35">
          <div
            className="h-full rounded-nf-ds-pill bg-white"
            style={{ width: `${percent}%` }}
          />
        </div>
        <span
          className="absolute size-nf-ds-3 -translate-x-1/2 rounded-nf-ds-pill bg-white shadow-nf-ds-sm"
          style={{ left: `${percent}%` }}
        />
      </div>
      <span className="shrink-0 font-nf-ds-mono text-nf-ds-rail tabular-nums text-white">
        {formatVideoTime(currentTime)} / {formatVideoTime(duration)}
      </span>
    </div>
  );
}
